import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { LOGO_BASE64 } from './logoData';

const G = rgb(0.102, 0.478, 0.29);   // #1a7a4a green
const N = rgb(0.102, 0.227, 0.361);  // #1a3a5c navy
const K = rgb(0, 0, 0);              // black
const W = rgb(1, 1, 1);              // white
const LG = rgb(0.95, 0.98, 0.95);    // light green bg
const GR = rgb(0.7, 0.7, 0.7);       // gray

const PW = 595.28;
const PH = 841.89;
const M = 20; // margin
/**
 * pdf-lib StandardFonts.Helvetica only supports WinAnsi (Latin-1).
 * This helper strips or replaces characters that would crash drawText.
 */
function safeText(str) {
  if (!str) return '';
  let val = String(str);
  // Replace newlines/tabs with spaces to avoid encode errors and keep words separated
  val = val.replace(/[\n\r\t]/g, ' ');
  // Basic Latin-1 support: chars 32-255. 
  // pdf-lib throws on characters it can't encode with the selected font.
  return val.replace(/[^\x20-\x7E\xA0-\xFF]/g, '');
}

function sectionHeader(page, text, x, y, w, fb) {
  page.drawRectangle({ x, y, width: w, height: 13, color: G });
  page.drawText(safeText(text).toUpperCase(), { x: x + 4, y: y + 3, size: 7, font: fb, color: W });
}

function fieldLine(page, label, value, x, y, w, f, fb) {
  const labelText = safeText(label).toUpperCase() + ':';
  const lw = Math.min(fb.widthOfTextAtSize(labelText, 5.5) + 2, w * 0.65);
  page.drawText(labelText, { x, y: y + 1, size: 5.5, font: fb, color: K });
  page.drawLine({ start: { x: x + lw + 2, y }, end: { x: x + w, y }, color: GR, thickness: 0.4 });
  if (value) page.drawText(safeText(value), { x: x + lw + 3, y: y + 1, size: 6.5, font: f, color: K });
}

function fullFieldLine(page, label, value, x, y, w, f, fb) {
  page.drawText(safeText(label).toUpperCase() + ':', { x, y: y + 9, size: 5.5, font: fb, color: K });
  const lineY = y;

  let finalY = y;
  if (value) {
    const valStr = String(value);
    const words = valStr.split(' ');
    let line = '';
    let cy = y + 1;
    // Let's wrap value if it goes beyond w
    words.forEach(wd => {
      const testLine = line ? line + ' ' + wd : wd;
      const testLineSafe = safeText(testLine);
      const textWidth = f.widthOfTextAtSize(testLineSafe, 6.5);
      if (textWidth > w - 4 && line) {
        page.drawText(safeText(line), { x: x + 2, y: cy, size: 6.5, font: f, color: K });
        page.drawLine({ start: { x, y: cy - 1 }, end: { x: x + w, y: cy - 1 }, color: GR, thickness: 0.4 });
        cy -= 9; // move down for next line
        line = wd;
      } else {
        line = testLine;
      }
    });
    if (line) {
      page.drawText(safeText(line), { x: x + 2, y: cy, size: 6.5, font: f, color: K });
      page.drawLine({ start: { x, y: cy - 1 }, end: { x: x + w, y: cy - 1 }, color: GR, thickness: 0.4 });
      finalY = cy - 9;
    }
  } else {
    // Empty field line
    page.drawLine({ start: { x, y }, end: { x: x + w, y }, color: GR, thickness: 0.4 });
    finalY = y - 9;
  }
  return finalY;
}

function chk(page, checked, x, y, size = 7) {
  page.drawRectangle({ x, y, width: size, height: size, borderColor: K, borderWidth: 0.5, color: checked ? G : W });
  if (checked) {
    page.drawLine({ start: { x: x + 1, y: y + 1 }, end: { x: x + size - 1, y: y + size - 1 }, color: W, thickness: 1 });
    page.drawLine({ start: { x: x + size - 1, y: y + 1 }, end: { x: x + 1, y: y + size - 1 }, color: W, thickness: 1 });
  }
}

function chkLabel(page, checked, label, x, y, f, fb, size = 7) {
  chk(page, checked, x, y, size);
  page.drawText(safeText(label).toUpperCase(), { x: x + size + 2, y: y + 1, size: 5.5, font: checked ? fb : f, color: K });
  return x + size + 2 + label.length * 3.8 + 6;
}

function borderBox(page, x, y, w, h) {
  page.drawRectangle({ x, y, width: w, height: h, borderColor: K, borderWidth: 0.4 });
}

// ─── Body zone definitions for PDF (geometric approximations) ─────────────────
// Each zone: [id, label, type ('e'=ellipse, 'r'=rect), ...params]
// Coordinate origin: top-left corner of the diagram bounding box at (bx, by)
// The diagram is scaled to fit within a given width (bodyW) and height (bodyH ~90pt)
const BODY_ZONE_MAP = {
  // FRONT
  cabeza: { type: 'e', cx: 0.50, cy: 0.085, rx: 0.145, ry: 0.085 },
  cara: { type: 'e', cx: 0.50, cy: 0.097, rx: 0.10, ry: 0.06 },
  cuello: { type: 'r', x: 0.435, y: 0.160, w: 0.13, h: 0.055 },
  hombro_der: { type: 'e', cx: 0.29, cy: 0.240, rx: 0.11, ry: 0.045 },
  hombro_izq: { type: 'e', cx: 0.71, cy: 0.240, rx: 0.11, ry: 0.045 },
  torax: { type: 'r', x: 0.33, y: 0.210, w: 0.34, h: 0.125 },
  abdomen: { type: 'r', x: 0.34, y: 0.330, w: 0.32, h: 0.115 },
  pelvis: { type: 'r', x: 0.33, y: 0.440, w: 0.34, h: 0.072 },
  brazo_der: { type: 'r', x: 0.15, y: 0.215, w: 0.135, h: 0.155 },
  brazo_izq: { type: 'r', x: 0.715, y: 0.215, w: 0.135, h: 0.155 },
  antebrazo_der: { type: 'r', x: 0.08, y: 0.380, w: 0.115, h: 0.140 },
  antebrazo_izq: { type: 'r', x: 0.805, y: 0.380, w: 0.115, h: 0.140 },
  mano_der: { type: 'e', cx: 0.135, cy: 0.580, rx: 0.075, ry: 0.050 },
  mano_izq: { type: 'e', cx: 0.865, cy: 0.580, rx: 0.075, ry: 0.050 },
  muslo_der: { type: 'r', x: 0.333, y: 0.512, w: 0.150, h: 0.185 },
  muslo_izq: { type: 'r', x: 0.517, y: 0.512, w: 0.150, h: 0.185 },
  rodilla_der: { type: 'e', cx: 0.408, cy: 0.720, rx: 0.085, ry: 0.038 },
  rodilla_izq: { type: 'e', cx: 0.592, cy: 0.720, rx: 0.085, ry: 0.038 },
  pierna_der: { type: 'r', x: 0.335, y: 0.745, w: 0.145, h: 0.175 },
  pierna_izq: { type: 'r', x: 0.520, y: 0.745, w: 0.145, h: 0.175 },
  pie_der: { type: 'e', cx: 0.400, cy: 0.960, rx: 0.120, ry: 0.040 },
  pie_izq: { type: 'e', cx: 0.600, cy: 0.960, rx: 0.120, ry: 0.040 },
  // BACK (same geometry, different labels — draw on right half if side-by-side)
  craneo_d: { type: 'e', cx: 0.50, cy: 0.085, rx: 0.145, ry: 0.085 },
  nuca: { type: 'r', x: 0.435, y: 0.160, w: 0.13, h: 0.055 },
  hombro_der_d: { type: 'e', cx: 0.29, cy: 0.240, rx: 0.11, ry: 0.045 },
  hombro_izq_d: { type: 'e', cx: 0.71, cy: 0.240, rx: 0.11, ry: 0.045 },
  espalda_alta: { type: 'r', x: 0.33, y: 0.210, w: 0.34, h: 0.105 },
  espalda_baja: { type: 'r', x: 0.33, y: 0.310, w: 0.34, h: 0.100 },
  lumbar: { type: 'r', x: 0.34, y: 0.405, w: 0.32, h: 0.075 },
  gluteo_der: { type: 'e', cx: 0.408, cy: 0.535, rx: 0.12, ry: 0.055 },
  gluteo_izq: { type: 'e', cx: 0.592, cy: 0.535, rx: 0.12, ry: 0.055 },
  brazo_der_d: { type: 'r', x: 0.15, y: 0.215, w: 0.135, h: 0.155 },
  brazo_izq_d: { type: 'r', x: 0.715, y: 0.215, w: 0.135, h: 0.155 },
  antebrazo_der_d: { type: 'r', x: 0.08, y: 0.380, w: 0.115, h: 0.140 },
  antebrazo_izq_d: { type: 'r', x: 0.805, y: 0.380, w: 0.115, h: 0.140 },
  mano_der_d: { type: 'e', cx: 0.135, cy: 0.580, rx: 0.075, ry: 0.050 },
  mano_izq_d: { type: 'e', cx: 0.865, cy: 0.580, rx: 0.075, ry: 0.050 },
  muslo_der_d: { type: 'r', x: 0.333, y: 0.590, w: 0.150, h: 0.165 },
  muslo_izq_d: { type: 'r', x: 0.517, y: 0.590, w: 0.150, h: 0.165 },
  pantorrilla_der: { type: 'r', x: 0.335, y: 0.760, w: 0.145, h: 0.160 },
  pantorrilla_izq: { type: 'r', x: 0.520, y: 0.760, w: 0.145, h: 0.160 },
  talon_der: { type: 'e', cx: 0.400, cy: 0.960, rx: 0.120, ry: 0.040 },
  talon_izq: { type: 'e', cx: 0.600, cy: 0.960, rx: 0.120, ry: 0.040 },
};

const ZONE_LABEL_MAP = {
  cabeza: 'Cabeza', cara: 'Cara', cuello: 'Cuello',
  hombro_der: 'Hombro Der.', hombro_izq: 'Hombro Izq.',
  torax: 'Tórax/Pecho', abdomen: 'Abdomen', pelvis: 'Pelvis/Cadera',
  brazo_der: 'Brazo Der.', brazo_izq: 'Brazo Izq.',
  antebrazo_der: 'Antebrazo Der.', antebrazo_izq: 'Antebrazo Izq.',
  mano_der: 'Mano Der.', mano_izq: 'Mano Izq.',
  muslo_der: 'Muslo Der.', muslo_izq: 'Muslo Izq.',
  rodilla_der: 'Rodilla Der.', rodilla_izq: 'Rodilla Izq.',
  pierna_der: 'Pierna Der.', pierna_izq: 'Pierna Izq.',
  pie_der: 'Pie Der.', pie_izq: 'Pie Izq.',
  craneo_d: 'Cráneo (D)', nuca: 'Nuca/Cervical',
  hombro_der_d: 'Hombro Der.(D)', hombro_izq_d: 'Hombro Izq.(D)',
  espalda_alta: 'Espalda Alta', espalda_baja: 'Espalda Baja',
  lumbar: 'Lumbar/Sacro', gluteo_der: 'Glúteo Der.', gluteo_izq: 'Glúteo Izq.',
  brazo_der_d: 'Brazo Der.(D)', brazo_izq_d: 'Brazo Izq.(D)',
  antebrazo_der_d: 'Antebrazo Der.(D)', antebrazo_izq_d: 'Antebrazo Izq.(D)',
  mano_der_d: 'Mano Der.(D)', mano_izq_d: 'Mano Izq.(D)',
  muslo_der_d: 'Muslo Der.(D)', muslo_izq_d: 'Muslo Izq.(D)',
  pantorrilla_der: 'Pantorrilla Der.', pantorrilla_izq: 'Pantorrilla Izq.',
  talon_der: 'Talón Der.', talon_izq: 'Talón Izq.',
};

// ── Organic SVG zone paths – viewBox 0 0 100 280 ──────────────────────────────
// Paths mirror the web BodyDiagram component exactly.
// Compound paths (central zones) = left half + mirrored right half.
const SVG_FRONT_ZONES = [
  { id: 'cabeza', d: 'M 50,10 C 42,10 36,15 36,25 C 36,32 38,36 41,39 C 45,36 48,35 50,35 Z M 50,10 C 58,10 64,15 64,25 C 64,32 62,36 59,39 C 55,36 52,35 50,35 Z' },
  { id: 'cara', d: 'M 41,39 C 43,44 46,47 50,47 C 49,43 47,40 50,35 C 48,35 45,36 41,39 Z M 59,39 C 57,44 54,47 50,47 C 51,43 53,40 50,35 C 52,35 55,36 59,39 Z' },
  { id: 'cuello', d: 'M 44,46 C 44,52 42,54 38,55 C 46,57 48,56 50,56 C 48,54 46,49 50,47 C 46,47 44,46 44,46 Z M 56,46 C 56,52 58,54 62,55 C 54,57 52,56 50,56 C 52,54 54,49 50,47 C 54,47 56,46 56,46 Z' },
  { id: 'torax', d: 'M 50,56 C 48,56 46,57 38,55 C 26,55 24,66 26,75 C 33,90 42,94 50,94 Z M 50,56 C 52,56 54,57 62,55 C 74,55 76,66 74,75 C 67,90 58,94 50,94 Z' },
  { id: 'abdomen', d: 'M 50,94 C 42,94 33,90 26,75 C 25,95 28,110 32,120 C 40,123 46,124 50,124 Z M 50,94 C 58,94 67,90 74,75 C 75,95 72,110 68,120 C 60,123 54,124 50,124 Z' },
  { id: 'pelvis', d: 'M 50,124 C 46,124 40,123 32,120 C 30,127 28,134 27,142 C 34,148 44,152 50,154 Z M 50,124 C 54,124 60,123 68,120 C 70,127 72,134 73,142 C 66,148 56,152 50,154 Z' },
  { id: 'hombro_izq', d: 'M 38,55 C 32,54 26,58 22,64 C 18,70 19,76 21,80 C 25,75 28,70 26,75 C 24,66 26,55 38,55 Z' },
  { id: 'hombro_der', d: 'M 62,55 C 68,54 74,58 78,64 C 82,70 81,76 79,80 C 75,75 72,70 74,75 C 76,66 74,55 62,55 Z' },
  { id: 'brazo_izq', d: 'M 21,80 C 18,88 17,96 17,104 C 20,108 24,106 27,104 C 28,95 28,85 26,75 C 28,70 25,75 21,80 Z' },
  { id: 'brazo_der', d: 'M 79,80 C 82,88 83,96 83,104 C 80,108 76,106 73,104 C 72,95 72,85 74,75 C 72,70 75,75 79,80 Z' },
  { id: 'antebrazo_izq', d: 'M 17,104 C 14,116 13,126 15,136 C 18,138 22,136 24,134 C 26,124 28,114 27,104 C 24,106 20,108 17,104 Z' },
  { id: 'antebrazo_der', d: 'M 83,104 C 86,116 87,126 85,136 C 82,138 78,136 76,134 C 74,124 72,114 73,104 C 76,106 80,108 83,104 Z' },
  { id: 'mano_izq', d: 'M 15,136 C 13,144 12,152 14,158 C 17,162 20,160 22,156 C 24,148 25,140 24,134 C 22,136 18,138 15,136 Z' },
  { id: 'mano_der', d: 'M 85,136 C 87,144 88,152 86,158 C 83,162 80,160 78,156 C 76,148 75,140 76,134 C 78,136 82,138 85,136 Z' },
  { id: 'muslo_izq', d: 'M 27,142 C 24,155 23,170 25,185 C 31,188 36,188 41,185 C 44,172 47,159 50,154 C 44,152 34,148 27,142 Z' },
  { id: 'muslo_der', d: 'M 73,142 C 76,155 77,170 75,185 C 69,188 64,188 59,185 C 56,172 53,159 50,154 C 56,152 66,148 73,142 Z' },
  { id: 'rodilla_izq', d: 'M 25,185 C 25,192 26,198 27,204 C 32,206 37,206 41,204 C 42,198 42,192 41,185 C 36,188 31,188 25,185 Z' },
  { id: 'rodilla_der', d: 'M 75,185 C 75,192 74,198 73,204 C 68,206 63,206 59,204 C 58,198 58,192 59,185 C 64,188 69,188 75,185 Z' },
  { id: 'pierna_izq', d: 'M 27,204 C 26,220 27,235 31,250 C 34,252 38,251 40,248 C 41,235 42,220 41,204 C 37,206 32,206 27,204 Z' },
  { id: 'pierna_der', d: 'M 73,204 C 74,220 73,235 69,250 C 66,252 62,251 60,248 C 59,235 58,220 59,204 C 63,206 68,206 73,204 Z' },
  { id: 'pie_izq', d: 'M 31,250 C 28,256 26,262 26,268 C 32,272 38,272 42,268 C 42,262 41,256 40,248 C 38,251 34,252 31,250 Z' },
  { id: 'pie_der', d: 'M 69,250 C 72,256 74,262 74,268 C 68,272 62,272 58,268 C 58,262 59,256 60,248 C 62,251 66,252 69,250 Z' },
];

const SVG_BACK_ZONES = [
  { id: 'craneo_d', d: 'M 50,10 C 42,10 36,15 36,25 C 36,32 38,36 41,39 C 45,38 48,37 50,37 Z M 50,10 C 58,10 64,15 64,25 C 64,32 62,36 59,39 C 55,38 52,37 50,37 Z' },
  { id: 'nuca', d: 'M 41,39 C 44,46 46,47 50,47 C 48,42 46,40 50,37 C 48,37 45,38 41,39 Z M 59,39 C 56,46 54,47 50,47 C 52,42 54,40 50,37 C 52,37 55,38 59,39 Z' },
  { id: 'espalda_alta', d: 'M 50,47 C 46,47 44,46 41,39 C 39,44 38,50 38,55 C 26,55 24,66 26,75 C 33,85 42,90 50,90 Z M 50,47 C 54,47 56,46 59,39 C 61,44 62,50 62,55 C 74,55 76,66 74,75 C 67,85 58,90 50,90 Z' },
  { id: 'espalda_baja', d: 'M 50,90 C 42,90 33,85 26,75 C 26,90 29,105 32,116 C 40,120 45,122 50,122 Z M 50,90 C 58,90 67,85 74,75 C 74,90 71,105 68,116 C 60,120 55,122 50,122 Z' },
  { id: 'lumbar', d: 'M 50,122 C 45,122 40,120 32,116 C 30,125 28,134 27,142 C 34,140 44,142 50,146 Z M 50,122 C 55,122 60,120 68,116 C 70,125 72,134 73,142 C 66,140 56,142 50,146 Z' },
  { id: 'hombro_izq_d', d: 'M 38,55 C 32,54 26,58 22,64 C 18,70 19,76 21,80 C 25,75 28,70 26,75 C 24,66 26,55 38,55 Z' },
  { id: 'hombro_der_d', d: 'M 62,55 C 68,54 74,58 78,64 C 82,70 81,76 79,80 C 75,75 72,70 74,75 C 76,66 74,55 62,55 Z' },
  { id: 'brazo_izq_d', d: 'M 21,80 C 18,88 17,96 17,104 C 20,108 24,106 27,104 C 28,95 28,85 26,75 C 28,70 25,75 21,80 Z' },
  { id: 'brazo_der_d', d: 'M 79,80 C 82,88 83,96 83,104 C 80,108 76,106 73,104 C 72,95 72,85 74,75 C 72,70 75,75 79,80 Z' },
  { id: 'antebrazo_izq_d', d: 'M 17,104 C 14,116 13,126 15,136 C 18,138 22,136 24,134 C 26,124 28,114 27,104 C 24,106 20,108 17,104 Z' },
  { id: 'antebrazo_der_d', d: 'M 83,104 C 86,116 87,126 85,136 C 82,138 78,136 76,134 C 74,124 72,114 73,104 C 76,106 80,108 83,104 Z' },
  { id: 'mano_izq_d', d: 'M 15,136 C 13,144 12,152 14,158 C 17,162 20,160 22,156 C 24,148 25,140 24,134 C 22,136 18,138 15,136 Z' },
  { id: 'mano_der_d', d: 'M 85,136 C 87,144 88,152 86,158 C 83,162 80,160 78,156 C 76,148 75,140 76,134 C 78,136 82,138 85,136 Z' },
  { id: 'gluteo_izq', d: 'M 27,142 C 24,155 23,170 25,185 C 31,188 36,188 41,185 C 44,172 47,159 50,146 C 44,142 34,140 27,142 Z' },
  { id: 'gluteo_der', d: 'M 73,142 C 76,155 77,170 75,185 C 69,188 64,188 59,185 C 56,172 53,159 50,146 C 56,142 66,140 73,142 Z' },
  { id: 'pantorrilla_izq', d: 'M 25,185 C 24,206 27,235 31,252 C 34,254 38,253 40,250 C 41,235 42,215 41,185 C 36,188 31,188 25,185 Z' },
  { id: 'pantorrilla_der', d: 'M 75,185 C 76,206 73,235 69,252 C 66,254 62,253 60,250 C 59,235 58,215 59,185 C 64,188 69,188 75,185 Z' },
  { id: 'talon_izq', d: 'M 31,252 C 29,258 28,264 28,270 C 33,272 37,272 41,270 C 41,264 40,258 40,250 C 38,253 34,254 31,252 Z' },
  { id: 'talon_der', d: 'M 69,252 C 71,258 72,264 72,270 C 67,272 63,272 59,270 C 59,264 60,258 60,250 C 62,253 66,254 69,252 Z' },
];

// Silhouette as a series of filled rectangles/ellipses at the body's own scale
const SILHOUETTE_PARTS = [
  // head
  { type: 'e', cx: 0.50, cy: 0.085, rx: 0.14, ry: 0.082, fill: [0.95, 0.88, 0.80] },
  // neck
  { type: 'r', x: 0.440, y: 0.160, w: 0.12, h: 0.050, fill: [0.92, 0.85, 0.77] },
  // shoulders
  { type: 'e', cx: 0.27, cy: 0.238, rx: 0.12, ry: 0.045, fill: [0.92, 0.85, 0.77] },
  { type: 'e', cx: 0.73, cy: 0.238, rx: 0.12, ry: 0.045, fill: [0.92, 0.85, 0.77] },
  // torso
  { type: 'r', x: 0.315, y: 0.205, w: 0.37, h: 0.275, fill: [0.93, 0.86, 0.78] },
  // upper arms
  { type: 'r', x: 0.140, y: 0.210, w: 0.155, h: 0.170, fill: [0.93, 0.86, 0.78], rx: 0.07 },
  { type: 'r', x: 0.705, y: 0.210, w: 0.155, h: 0.170, fill: [0.93, 0.86, 0.78], rx: 0.07 },
  // forearms
  { type: 'r', x: 0.072, y: 0.375, w: 0.125, h: 0.155, fill: [0.92, 0.85, 0.77], rx: 0.06 },
  { type: 'r', x: 0.803, y: 0.375, w: 0.125, h: 0.155, fill: [0.92, 0.85, 0.77], rx: 0.06 },
  // hands
  { type: 'e', cx: 0.130, cy: 0.578, rx: 0.078, ry: 0.055, fill: [0.92, 0.85, 0.77] },
  { type: 'e', cx: 0.870, cy: 0.578, rx: 0.078, ry: 0.055, fill: [0.92, 0.85, 0.77] },
  // thighs
  { type: 'r', x: 0.330, y: 0.478, w: 0.155, h: 0.200, fill: [0.93, 0.86, 0.78], rx: 0.075 },
  { type: 'r', x: 0.515, y: 0.478, w: 0.155, h: 0.200, fill: [0.93, 0.86, 0.78], rx: 0.075 },
  // knees
  { type: 'e', cx: 0.408, cy: 0.718, rx: 0.088, ry: 0.040, fill: [0.91, 0.84, 0.76] },
  { type: 'e', cx: 0.592, cy: 0.718, rx: 0.088, ry: 0.040, fill: [0.91, 0.84, 0.76] },
  // lower legs
  { type: 'r', x: 0.333, y: 0.748, w: 0.148, h: 0.188, fill: [0.93, 0.86, 0.78], rx: 0.07 },
  { type: 'r', x: 0.519, y: 0.748, w: 0.148, h: 0.188, fill: [0.93, 0.86, 0.78], rx: 0.07 },
  // feet
  { type: 'e', cx: 0.400, cy: 0.960, rx: 0.118, ry: 0.042, fill: [0.91, 0.84, 0.76] },
  { type: 'e', cx: 0.600, cy: 0.960, rx: 0.118, ry: 0.042, fill: [0.91, 0.84, 0.76] },
];

function drawBodyDiagram(page, zonas, x, y, w, h, f, fb) {
  // y is the top-left corner (pdf-lib y-axis goes up)
  // h is the total height available for the section (including header)
  const headerH = 13;
  sectionHeader(page, 'Zona de Lesión', x, y - headerH, w, fb);

  // Allocate space: two miniature figures side-by-side
  const figPad = 4;
  const figW = Math.floor((w - 3 * figPad) / 2); // width of each figure
  const figH = h - headerH - 6;                   // height available for figures

  // Draw label under each figure
  const labelY = y - headerH - figH - 2;

  for (let side = 0; side < 2; side++) {
    const fx = x + figPad + side * (figW + figPad); // top-left x of figure
    const fy = y - headerH - 3;                     // top-left y of figure (pdf y goes up, so this is the top)
    const fw = figW;
    const fh = figH;

    // Figure title
    const title = side === 0 ? 'ANTERIOR' : 'POSTERIOR';
    page.drawText(title, { x: fx + fw / 2 - fb.widthOfTextAtSize(title, 4) / 2, y: fy - 5, size: 4, font: fb, color: N });

    // Determine which zone ids belong to this side
    const isFront = side === 0;
    const frontIds = ['cabeza', 'cara', 'cuello', 'hombro_der', 'hombro_izq', 'torax', 'abdomen', 'pelvis',
      'brazo_der', 'brazo_izq', 'antebrazo_der', 'antebrazo_izq', 'mano_der', 'mano_izq',
      'muslo_der', 'muslo_izq', 'rodilla_der', 'rodilla_izq', 'pierna_der', 'pierna_izq', 'pie_der', 'pie_izq'];
    const backIds = Object.keys(BODY_ZONE_MAP).filter(k => !frontIds.includes(k));
    const sideIds = isFront ? frontIds : backIds;

    // Helper: convert normalized coords to PDF coords
    const figBodyH = fh - 8; // leave 8pt for title
    const figBodyY = fy - 8; // start of figure body below title
    const px = (nx) => fx + nx * fw;
    const py = (ny) => figBodyY - ny * figBodyH;

    // Draw silhouette parts
    SILHOUETTE_PARTS.forEach(part => {
      const fc = rgb(part.fill[0], part.fill[1], part.fill[2]);
      if (part.type === 'e') {
        page.drawEllipse({
          x: px(part.cx), y: py(part.cy),
          xScale: part.rx * fw, yScale: part.ry * figBodyH,
          color: fc, borderColor: rgb(0.78, 0.68, 0.58), borderWidth: 0.3,
        });
      } else {
        page.drawRectangle({
          x: px(part.x), y: py(part.y + part.h), // pdf-lib rect: y is bottom-left
          width: part.w * fw, height: part.h * figBodyH,
          color: fc, borderColor: rgb(0.78, 0.68, 0.58), borderWidth: 0.3,
          ...(part.rx ? {} : {}),
        });
      }
    });

    // Draw anatomical detail lines (decorative)
    if (isFront) {
      // Sternum
      const sternX = px(0.50);
      page.drawLine({ start: { x: sternX, y: py(0.218) }, end: { x: sternX, y: py(0.330) }, color: rgb(0.75, 0.65, 0.55), thickness: 0.3 });
      // Clavicles
      page.drawLine({ start: { x: px(0.385), y: py(0.220) }, end: { x: px(0.50), y: py(0.210) }, color: rgb(0.75, 0.65, 0.55), thickness: 0.3 });
      page.drawLine({ start: { x: px(0.615), y: py(0.220) }, end: { x: px(0.50), y: py(0.210) }, color: rgb(0.75, 0.65, 0.55), thickness: 0.3 });
      // Navel (small circle)
      page.drawEllipse({ x: px(0.50), y: py(0.390), xScale: 1.2, yScale: 1.2, color: rgb(0.75, 0.65, 0.55) });
      // Eyes
      page.drawEllipse({ x: px(0.46), y: py(0.092), xScale: 1.8, yScale: 1.5, color: rgb(0.60, 0.78, 0.95) });
      page.drawEllipse({ x: px(0.54), y: py(0.092), xScale: 1.8, yScale: 1.5, color: rgb(0.60, 0.78, 0.95) });
    } else {
      // Spine dashes
      for (let s = 0; s < 8; s++) {
        const spY = 0.210 + s * 0.030;
        page.drawEllipse({ x: px(0.50), y: py(spY), xScale: 1.0, yScale: 1.2, color: rgb(0.75, 0.65, 0.55) });
      }
    }

    // Highlight selected zones
    const RED_FILL = rgb(0.86, 0.10, 0.10);
    sideIds.forEach(id => {
      if (!zonas.includes(id)) return;
      const z = BODY_ZONE_MAP[id];
      if (!z) return;
      if (z.type === 'e') {
        page.drawEllipse({
          x: px(z.cx), y: py(z.cy),
          xScale: z.rx * fw, yScale: z.ry * figBodyH,
          color: rgb(0.86, 0.10, 0.10),
          opacity: 0.55,
          borderColor: rgb(0.72, 0.05, 0.05), borderWidth: 0.7,
        });
      } else {
        page.drawRectangle({
          x: px(z.x), y: py(z.y + z.h),
          width: z.w * fw, height: z.h * figBodyH,
          color: RED_FILL, opacity: 0.50,
          borderColor: rgb(0.72, 0.05, 0.05), borderWidth: 0.7,
        });
      }
    });
  }

  // Zone list as text column to the right of the two figures (if narrower layout needed)
  // Instead, draw the affected zone names below both figures as a compact list
  const affectedLabels = zonas.map(id => ZONE_LABEL_MAP[id]).filter(Boolean);
  if (affectedLabels.length > 0) {
    const listY = labelY - 2;
    page.drawText('ZONAS AFECTADAS:', { x, y: listY, size: 5, font: fb, color: N });
    let lx = x;
    let ly = listY - 7;
    affectedLabels.forEach((lbl, i) => {
      const chip = `• ${lbl}`;
      const chipW = f.widthOfTextAtSize(chip, 5) + 6;
      if (lx + chipW > x + w) { lx = x; ly -= 7; }
      page.drawRectangle({ x: lx, y: ly - 1, width: chipW, height: 6.5, color: rgb(0.98, 0.93, 0.93), borderColor: rgb(0.86, 0.10, 0.10), borderWidth: 0.4 });
      page.drawText(chip, { x: lx + 2, y: ly + 0.5, size: 4.5, font: f, color: rgb(0.72, 0.05, 0.05) });
      lx += chipW + 2;
    });
  }
}


async function fetchLogoImage(doc) {
  try {
    const binaryStr = atob(LOGO_BASE64);
    const bytes = new Uint8Array(binaryStr.length);
    for (let i = 0; i < binaryStr.length; i++) bytes[i] = binaryStr.charCodeAt(i);
    return await doc.embedJpg(bytes.buffer);
  } catch {
    return null;
  }
}

async function embedSignature(doc, dataUrl) {
  if (!dataUrl) return null;
  try {
    const base64 = dataUrl.split(',')[1];
    const buf = Uint8Array.from(atob(base64), c => c.charCodeAt(0)).buffer;
    return await doc.embedPng(buf);
  } catch {
    return null;
  }
}

export async function generateFrapPdf(form) {
  const doc = await PDFDocument.create();
  const f = await doc.embedFont(StandardFonts.Helvetica);
  const fb = await doc.embedFont(StandardFonts.HelveticaBold);

  const logo = await fetchLogoImage(doc);

  // ─────────────── PAGE 1 ───────────────
  const p1 = doc.addPage([PW, PH]);

  // ── HEADER ──
  let hy = PH - M;
  // Header border
  borderBox(p1, M, hy - 60, PW - 2 * M, 60);
  // Logo
  if (logo) {
    p1.drawImage(logo, { x: M + 2, y: hy - 58, width: 52, height: 52 });
  }
  // Org name
  p1.drawText('CUERPO DE RESCATE DE ENSENADA, A.C.', { x: M + 60, y: hy - 14, size: 10, font: fb, color: N });
  p1.drawText('Calle Magnolias No. 2356', { x: M + 60, y: hy - 25, size: 7, font: f, color: K });
  p1.drawText('Col. Márquez de León, Ensenada, B.C.', { x: M + 60, y: hy - 33, size: 7, font: f, color: K });
  p1.drawText('Tel. 646 152 4659', { x: M + 60, y: hy - 41, size: 7, font: f, color: K });
  // Date / Folio box
  const dbx = PW - M - 100;
  borderBox(p1, dbx, hy - 58, 100, 58);
  p1.drawText('FECHA:', { x: dbx + 4, y: hy - 15, size: 7, font: fb, color: K });
  p1.drawText(safeText(form.fecha || new Date().toLocaleDateString('es-MX')), { x: dbx + 32, y: hy - 15, size: 7, font: f, color: K });
  p1.drawText('# FOLIO:', { x: dbx + 4, y: hy - 32, size: 7, font: fb, color: K });
  p1.drawText(safeText(form.folio || '----'), { x: dbx + 32, y: hy - 32, size: 14, font: fb, color: N });
  hy -= 62;

  // ── TIMES ROW ──
  const times = [
    ['HORA LLAMADA', form.hora_llamada],
    ['HORA SALIDA', form.hora_salida],
    ['HORA LLEGADA', form.hora_llegada],
    ['HORA TRASLADO', form.hora_traslado],
    ['HORA HOSPITAL', form.hora_hospital],
    ['HORA BASE', form.hora_base],
  ];
  const tw = (PW - 2 * M) / 6;
  times.forEach(([lbl, val], i) => {
    const tx = M + i * tw;
    p1.drawRectangle({ x: tx, y: hy - 22, width: tw, height: 22, borderColor: K, borderWidth: 0.4, color: i === 0 ? LG : W });
    p1.drawText(lbl.toUpperCase(), { x: tx + 2, y: hy - 8, size: 5, font: fb, color: N });
    if (val) p1.drawText(safeText(val), { x: tx + 2, y: hy - 17, size: 9, font: f, color: K });
  });
  hy -= 26;

  // ── DATOS DEL INCIDENTE ──
  sectionHeader(p1, 'Datos del Incidente', M, hy - 13, PW - 2 * M, fb);
  hy -= 16;
  p1.drawText('MOTIVO:', { x: M, y: hy - 2, size: 5.5, font: fb, color: K });
  let ix = M + 36;
  ix = chkLabel(p1, form.motivo_atencion === 'TRAUMA', 'Trauma', ix, hy - 8, f, fb);
  ix = chkLabel(p1, form.motivo_atencion === 'ENFERMEDAD', 'Enfermedad', ix, hy - 8, f, fb);
  ix = chkLabel(p1, form.motivo_atencion === 'GINECO-OBST.', 'Gineco-Obst.', ix, hy - 8, f, fb);
  ix = chkLabel(p1, form.motivo_atencion === 'PSIQUIÁTRICO', 'Psiquiátrico', ix, hy - 8, f, fb);
  chkLabel(p1, form.motivo_atencion === 'OTRO', 'Otro', ix, hy - 8, f, fb);
  hy -= 12;

  p1.drawText('LUGAR DE OCURRENCIA:', { x: M, y: hy - 2, size: 5.5, font: fb, color: K });
  ix = M + 80;
  ix = chkLabel(p1, form.lugar_ocurrencia_vp, 'Vía Pública', ix, hy - 8, f, fb);
  ix = chkLabel(p1, form.lugar_ocurrencia_hogar, 'Hogar', ix, hy - 8, f, fb);
  ix = chkLabel(p1, form.lugar_ocurrencia_trabajo, 'Trabajo', ix, hy - 8, f, fb);
  ix = chkLabel(p1, form.lugar_ocurrencia_escuela, 'Escuela', ix, hy - 8, f, fb);
  ix = chkLabel(p1, form.lugar_ocurrencia_recreativo, 'Centro Recreativo', ix, hy - 8, f, fb);
  ix = chkLabel(p1, form.lugar_ocurrencia_otro, 'Otro', ix, hy - 8, f, fb);
  if (form.lugar_ocurrencia_otro && form.lugar_ocurrencia_otro_texto) {
    p1.drawText(safeText(form.lugar_ocurrencia_otro_texto), { x: ix, y: hy - 8, size: 6.5, font: f, color: K });
    p1.drawLine({ start: { x: ix - 2, y: hy - 10 }, end: { x: ix + 60, y: hy - 10 }, color: GR, thickness: 0.4 });
  }
  hy -= 12;

  fieldLine(p1, 'Calle / Ubicación', form.incidente_calle, M, hy - 4, 200, f, fb);
  fieldLine(p1, 'Colonia', form.incidente_colonia, M + 204, hy - 4, 150, f, fb);
  fieldLine(p1, 'Delegación / Municipio', form.incidente_delegacion, M + 360, hy - 4, PW - 2 * M - 360, f, fb);
  hy -= 12;

  fieldLine(p1, 'Operador', form.operador, M, hy - 4, 200, f, fb);
  fieldLine(p1, 'Prestadores de Servicio', form.prestadores_servicio, M + 204, hy - 4, PW - 2 * M - 204, f, fb);
  hy -= 16;

  // ── LEFT COLUMN ──
  const LX = M;
  const LW = 262;
  let ly = hy;

  // NOMBRE PACIENTE
  sectionHeader(p1, 'Nombre del Paciente', LX, ly - 13, LW, fb);
  ly -= 13;
  fullFieldLine(p1, 'Nombre del Paciente', form.nombre_paciente, LX, ly - 18, LW, f, fb);
  ly -= 22;
  fullFieldLine(p1, 'Nombre del Acompañante', form.nombre_acompanante, LX, ly - 14, LW, f, fb);
  ly -= 20;

  // Sexo + Edad
  p1.drawText('SEXO:', { x: LX, y: ly - 2, size: 5.5, font: fb, color: K });
  let sx = LX + 24;
  sx = chkLabel(p1, form.sexo === 'MASC', 'MASC.', sx, ly - 8, f, fb);
  chkLabel(p1, form.sexo === 'FEM', 'FEM.', sx, ly - 8, f, fb);
  p1.drawText('EDAD:', { x: LX + 90, y: ly - 2, size: 5.5, font: fb, color: K });
  borderBox(p1, LX + 112, ly - 10, 22, 10);
  if (form.edad_anios) p1.drawText(String(form.edad_anios), { x: LX + 113, y: ly - 8, size: 7, font: f, color: K });
  p1.drawText('AÑOS', { x: LX + 136, y: ly - 8, size: 5.5, font: fb, color: K });
  borderBox(p1, LX + 158, ly - 10, 22, 10);
  if (form.edad_meses) p1.drawText(String(form.edad_meses), { x: LX + 159, y: ly - 8, size: 7, font: f, color: K });
  p1.drawText('MESES', { x: LX + 182, y: ly - 8, size: 5.5, font: fb, color: K });
  ly -= 14;

  fullFieldLine(p1, 'Domicilio', form.domicilio, LX, ly - 12, LW, f, fb);
  ly -= 16;
  fullFieldLine(p1, 'Colonia / Comunidad', form.colonia, LX, ly - 12, LW, f, fb);
  ly -= 16;
  fullFieldLine(p1, 'Delegación Política / Municipio', form.delegacion, LX, ly - 12, LW, f, fb);
  ly -= 16;
  fieldLine(p1, 'Teléfono', form.telefono, LX, ly - 4, 110, f, fb);
  fieldLine(p1, 'Ocupación', form.ocupacion, LX + 116, ly - 4, LW - 116, f, fb);
  ly -= 14;
  fullFieldLine(p1, 'Derechohabiente a', form.derechohabiente, LX, ly - 12, LW, f, fb);
  ly -= 16;
  fullFieldLine(p1, 'Compañía de Seguros Gastos Médicos', form.compania_seguros, LX, ly - 12, LW, f, fb);
  ly -= 18;

  // ORIGEN PROBABLE
  sectionHeader(p1, 'Origen Probable', LX, ly - 13, LW, fb);
  ly -= 16;
  let ox = LX;
  ox = chkLabel(p1, form.origen_neurologia, 'Neurología', ox, ly - 8, f, fb);
  ox = chkLabel(p1, form.origen_digestiva, 'Digestiva', ox, ly - 8, f, fb);
  chkLabel(p1, form.origen_intoxicacion, 'Intoxicación', ox, ly - 8, f, fb);
  ly -= 12;
  ox = LX;
  ox = chkLabel(p1, form.origen_cardiovascular, 'Cardiovascular', ox, ly - 8, f, fb);
  ox = chkLabel(p1, form.origen_urogenital, 'Urogenital', ox, ly - 8, f, fb);
  chkLabel(p1, form.origen_infecciosa, 'Infecciosa', ox, ly - 8, f, fb);
  ly -= 12;
  ox = LX;
  ox = chkLabel(p1, form.origen_respiratorio, 'Respiratorio', ox, ly - 8, f, fb);
  ox = chkLabel(p1, form.origen_gineco, 'Gineco-Obstétrica', ox, ly - 8, f, fb);
  chkLabel(p1, form.origen_oncologico, 'Oncológico', ox, ly - 8, f, fb);
  ly -= 12;
  ox = LX;
  ox = chkLabel(p1, form.origen_metabolico, 'Metabólico', ox, ly - 8, f, fb);
  ox = chkLabel(p1, form.origen_cognitivo, 'Cognitivo Emocional', ox, ly - 8, f, fb);
  chkLabel(p1, form.origen_otro, 'Otro', ox, ly - 8, f, fb);
  ly -= 16;
  fullFieldLine(p1, 'Especifique', form.origen_especifique, LX, ly - 10, LW, f, fb);
  ly -= 14;
  p1.drawText('1A VEZ:', { x: LX, y: ly - 2, size: 5.5, font: fb, color: K });
  borderBox(p1, LX + 25, ly - 10, 18, 10);
  if (form.primera_vez) p1.drawText('X', { x: LX + 29, y: ly - 8, size: 7, font: fb, color: K });
  p1.drawText('SUBSECUENTE:', { x: LX + 50, y: ly - 2, size: 5.5, font: fb, color: K });
  borderBox(p1, LX + 100, ly - 10, 18, 10);
  if (form.subsecuente) p1.drawText('X', { x: LX + 104, y: ly - 8, size: 7, font: fb, color: K });
  ly -= 16;

  // ACCIDENTE AUTOMOVILÍSTICO
  sectionHeader(p1, 'Accidente Automovilístico', LX, ly - 13, LW, fb);
  ly -= 16;
  ox = LX;
  ox = chkLabel(p1, form.acc_colision, 'Colisión', ox, ly - 8, f, fb);
  chkLabel(p1, form.acc_volcadura, 'Volcadura', ox, ly - 8, f, fb);
  ly -= 12;
  ox = LX;
  ox = chkLabel(p1, form.acc_col_automotor, 'Automotor', ox, ly - 8, f, fb);
  ox = chkLabel(p1, form.acc_col_moto, 'Motocicleta', ox, ly - 8, f, fb);
  ox = chkLabel(p1, form.acc_col_bici, 'Bicicleta', ox, ly - 8, f, fb);
  chkLabel(p1, form.acc_col_maquinaria, 'Maquinaria', ox, ly - 8, f, fb);
  ly -= 14;

  p1.drawText('CONTRA OBJETO:', { x: LX, y: ly - 2, size: 5.5, font: fb, color: K });
  ox = LX + 56;
  ox = chkLabel(p1, form.contra_fijo, 'Fijo', ox, ly - 8, f, fb);
  chkLabel(p1, form.contra_movimiento, 'En Movimiento', ox, ly - 8, f, fb);
  ly -= 12;

  p1.drawText('IMPACTO:', { x: LX, y: ly - 2, size: 5.5, font: fb, color: K });
  ox = LX + 32;
  ox = chkLabel(p1, form.impacto_frontal, 'Frontal', ox, ly - 8, f, fb);
  ox = chkLabel(p1, form.impacto_lateral, 'Lateral', ox, ly - 8, f, fb);
  chkLabel(p1, form.impacto_posterior, 'Posterior', ox, ly - 8, f, fb);
  ly -= 14;

  p1.drawText('HUNDIMIENTO:', { x: LX, y: ly - 2, size: 5.5, font: fb, color: K });
  fieldLine(p1, '', form.hundimiento_cms, LX + 52, ly - 4, 30, f, fb);
  p1.drawText('CMS', { x: LX + 86, y: ly - 2, size: 5.5, font: fb, color: K });
  p1.drawText('PARABRISAS:', { x: LX + 110, y: ly - 2, size: 5.5, font: fb, color: K });
  ox = LX + 154;
  ox = chkLabel(p1, form.parabrisas_roto, 'Roto', ox, ly - 8, f, fb);
  chkLabel(p1, form.parabrisas_doblado, 'Doblado', ox, ly - 8, f, fb);
  ly -= 14;

  p1.drawText('VOLANTE:', { x: LX, y: ly - 2, size: 5.5, font: fb, color: K });
  ox = LX + 34;
  ox = chkLabel(p1, form.volante_integro, 'Íntegro', ox, ly - 8, f, fb);
  ox = chkLabel(p1, form.volante_doblado, 'Doblado', ox, ly - 8, f, fb);
  p1.drawText('BOLSA AIRE:', { x: ox + 4, y: ly - 2, size: 5.5, font: fb, color: K });
  ox += 48;
  ox = chkLabel(p1, form.bolsa_si, 'Sí', ox, ly - 8, f, fb);
  chkLabel(p1, form.bolsa_no, 'No', ox, ly - 8, f, fb);
  ly -= 14;

  p1.drawText('CINTURÓN:', { x: LX, y: ly - 2, size: 5.5, font: fb, color: K });
  ox = LX + 36;
  ox = chkLabel(p1, form.cinturon_colocado, 'Colocado', ox, ly - 8, f, fb);
  chkLabel(p1, form.cinturon_no, 'No Colocado', ox, ly - 8, f, fb);
  ly -= 12;

  p1.drawText('DENTRO VEH.:', { x: LX, y: ly - 2, size: 5.5, font: fb, color: K });
  ox = LX + 46;
  ox = chkLabel(p1, form.dentro_si, 'Sí', ox, ly - 8, f, fb);
  ox = chkLabel(p1, form.dentro_no, 'No', ox, ly - 8, f, fb);
  chkLabel(p1, form.eyectado, 'Eyectado', ox, ly - 8, f, fb);
  ly -= 12;

  p1.drawText('ATROPELLADO:', { x: LX, y: ly - 2, size: 5.5, font: fb, color: K });
  ox = LX + 52;
  ox = chkLabel(p1, form.atrop_automotor, 'Automotor', ox, ly - 8, f, fb);
  ox = chkLabel(p1, form.atrop_moto, 'Motocicleta', ox, ly - 8, f, fb);
  ox = chkLabel(p1, form.atrop_bici, 'Bicicleta', ox, ly - 8, f, fb);
  chkLabel(p1, form.atrop_maquinaria, 'Maquinaria', ox, ly - 8, f, fb);
  ly -= 12;

  p1.drawText('CASCO SEGURIDAD:', { x: LX, y: ly - 2, size: 5.5, font: fb, color: K });
  ox = LX + 64;
  ox = chkLabel(p1, form.casco_si, 'Sí', ox, ly - 8, f, fb);
  chkLabel(p1, form.casco_no, 'No', ox, ly - 8, f, fb);
  ly -= 16;

  // AGENTE CAUSAL
  sectionHeader(p1, 'Agente Causal', LX, ly - 13, LW, fb);
  ly -= 16;
  const agentes = [
    [form.agente_arma, 'Arma'], [form.agente_maquinaria, 'Maquinaria'], [form.agente_electricidad, 'Electricidad'],
    [form.agente_juguete, 'Juguete'], [form.agente_herramienta, 'Herramienta'], [form.agente_explosion, 'Explosión'],
    [form.agente_automotor, 'Automotor'], [form.agente_fuego, 'Fuego'], [form.agente_ser_humano, 'Ser Humano'],
    [form.agente_bicicleta, 'Bicicleta'], [form.agente_sust_caliente, 'Sust. Caliente'], [form.agente_animal, 'Animal'],
    [form.agente_prod_biologico, 'Prod. Biológico'], [form.agente_sust_toxica, 'Sust. Tóxica'], [form.agente_otro, 'Otro'],
  ];
  for (let row = 0; row < 5; row++) {
    ox = LX;
    for (let col = 0; col < 3; col++) {
      const idx = row * 3 + col;
      if (idx < agentes.length) {
        ox = chkLabel(p1, agentes[idx][0], agentes[idx][1], ox, ly - 8, f, fb);
      }
    }
    ly -= 11;
  }
  ly -= 4; // Add extra padding before especifique
  fullFieldLine(p1, 'Especifique', form.agente_especifique, LX, ly - 10, LW, f, fb);
  ly -= 14;
  fullFieldLine(p1, 'Lesiones causadas por', form.lesiones_causadas_por, LX, ly - 10, LW, f, fb);
  ly -= 16;



  // ── RIGHT COLUMN ──
  const RX = M + LW + 13;
  const RW = PW - RX - M;
  let ry = hy;

  // CONDICIÓN DEL PACIENTE
  sectionHeader(p1, 'Condición del Paciente', RX, ry - 13, RW, fb);
  ry -= 16;
  let ro = RX;
  ro = chkLabel(p1, form.cond_critico_inestable, 'Crítico Inestable', ro, ry - 8, f, fb);
  chkLabel(p1, form.cond_no_critico, 'No Crítico', ro, ry - 8, f, fb);
  ry -= 12;
  chkLabel(p1, form.cond_critico_estable, 'Crítico Estable', RX, ry - 8, f, fb);
  ry -= 16;

  // PRIORIDAD
  sectionHeader(p1, 'Prioridad', RX, ry - 13, RW, fb);
  ry -= 16;
  const prioColors = { ROJO: rgb(0.86, 0.1, 0.1), VERDE: G, AMARILLO: rgb(0.85, 0.65, 0), NEGRO: K };
  const prios = ['ROJO', 'VERDE', 'AMARILLO', 'NEGRO'];
  ro = RX;
  prios.forEach(p => {
    const sel = form.prioridad === p;
    p1.drawRectangle({ x: ro, y: ry - 12, width: 42, height: 12, color: sel ? prioColors[p] : W, borderColor: prioColors[p], borderWidth: 1 });
    p1.drawText(p, { x: ro + 4, y: ry - 8, size: 7, font: fb, color: sel ? W : prioColors[p] });
    ro += 46;
  });
  ry -= 18;

  // VÍA AÉREA + CONTROL CERVICAL
  sectionHeader(p1, 'Vía Aérea', RX, ry - 13, (RW / 2) - 2, fb);
  sectionHeader(p1, 'Control Cervical', RX + RW / 2 + 2, ry - 13, RW / 2 - 2, fb);
  ry -= 16;
  chkLabel(p1, form.via_aspiracion, 'Aspiración', RX, ry - 8, f, fb);
  chkLabel(p1, form.cervical_manual, 'Manual', RX + RW / 2 + 2, ry - 8, f, fb);
  ry -= 11;
  chkLabel(p1, form.via_orofaringea, 'Cánula Orofaringea', RX, ry - 8, f, fb);
  chkLabel(p1, form.cervical_rigido, 'Collarín Rígido', RX + RW / 2 + 2, ry - 8, f, fb);
  ry -= 11;
  chkLabel(p1, form.via_nasofaringea, 'Cánula Nasofaringea', RX, ry - 8, f, fb);
  chkLabel(p1, form.cervical_blando, 'Collarín Blando', RX + RW / 2 + 2, ry - 8, f, fb);
  ry -= 16;

  // ASISTENCIA VENTILATORIA / OXIGENOTERAPIA
  sectionHeader(p1, 'Asistencia Ventilatoria / Oxigenoterapia', RX, ry - 13, RW, fb);
  ry -= 16;
  ro = RX;
  ro = chkLabel(p1, form.vent_balon, 'Balón-Válvula Mascarilla', ro, ry - 8, f, fb);
  ry -= 11;
  ro = RX;
  ro = chkLabel(p1, form.vent_auto, 'Ventilador Automático', ro, ry - 8, f, fb);
  fieldLine(p1, 'Frec', form.vent_frec, ro + 2, ry - 4, 38, f, fb);
  fieldLine(p1, 'Vol', form.vent_vol, ro + 44, ry - 4, 38, f, fb);
  ry -= 11;
  ro = RX;
  ro = chkLabel(p1, form.vent_puntas, 'Puntas Nasales', ro, ry - 8, f, fb);
  ro = chkLabel(p1, form.vent_mascarilla_simple, 'Mascarilla Simple', ro, ry - 8, f, fb);
  ry -= 11;
  ro = RX;
  ro = chkLabel(p1, form.vent_mascarilla_res, 'Mascarilla con Reservorio', ro, ry - 8, f, fb);
  fieldLine(p1, 'LTS x MIN', form.vent_lts_min, ro + 2, ry - 4, 50, f, fb);
  ry -= 16;

  // CONTROL DE HEMORRAGIAS
  sectionHeader(p1, 'Control de Hemorragias / Vías Venosas', RX, ry - 13, RW, fb);
  ry -= 16;
  ro = RX;
  ro = chkLabel(p1, form.hem_presion_directa, 'Presión Directa', ro, ry - 8, f, fb);
  ro = chkLabel(p1, form.hem_torniquete, 'Torniquete', ro, ry - 8, f, fb);
  chkLabel(p1, form.hem_vendaje, 'Vendaje Compresivo', ro, ry - 8, f, fb);
  ry -= 11;
  fieldLine(p1, 'Vía IV #', form.hem_via_iv, RX, ry - 4, 70, f, fb);
  fieldLine(p1, 'Catéter #', form.hem_cateter, RX + 74, ry - 4, 70, f, fb);
  ry -= 12;
  p1.drawText('SITIO:', { x: RX, y: ry - 2, size: 5.5, font: fb, color: K });
  ro = RX + 22;
  ro = chkLabel(p1, form.hem_sitio_mano, 'Mano', ro, ry - 8, f, fb);
  chkLabel(p1, form.hem_sitio_pliegue, 'Pliegue Antecubital', ro, ry - 8, f, fb);
  ry -= 11;
  p1.drawText('SOLUCIONES:', { x: RX, y: ry - 2, size: 5.5, font: fb, color: K });
  ro = RX + 46;
  ro = chkLabel(p1, form.sol_hartmann, 'Hartmann', ro, ry - 8, f, fb);
  ro = chkLabel(p1, form.sol_nacl, 'NaCl 0.9%', ro, ry - 8, f, fb);
  chkLabel(p1, form.sol_mixta, 'Mixta', ro, ry - 8, f, fb);
  ry -= 11;
  ro = RX + 46;
  ro = chkLabel(p1, form.sol_glucosa, 'Glucosa 5%', ro, ry - 8, f, fb);
  chkLabel(p1, form.sol_otra, 'Otra', ro, ry - 8, f, fb);
  ry -= 16;

  // INTERROGATORIO
  sectionHeader(p1, 'Interrogatorio', RX, ry - 13, RW, fb);
  ry -= 18; // Increased from 16 to give Alergias more breathing room
  fullFieldLine(p1, 'Alergias', form.alergias, RX, ry - 10, RW, f, fb);
  ry -= 14;
  fullFieldLine(p1, 'Medicamentos que está ingiriendo', form.medicamentos, RX, ry - 10, RW, f, fb);
  ry -= 14;
  fullFieldLine(p1, 'Enfermedades y cirugías previas', form.enfermedades_previas, RX, ry - 10, RW, f, fb);
  ry -= 14;
  fullFieldLine(p1, 'Hora de la última comida', form.hora_ultima_comida, RX, ry - 10, RW, f, fb);
  ry -= 14;
  fullFieldLine(p1, 'Eventos previos relacionados', form.eventos_previos, RX, ry - 10, RW, f, fb);
  ry -= 16;

  // OBSERVACIONES
  sectionHeader(p1, 'Observaciones', RX, ry - 13, RW, fb);
  ry -= 13;
  // Increase box height from 30 to 40 to allow more lines of text
  borderBox(p1, RX, ry - 40, RW, 40);
  if (form.observaciones) {
    const safeObs = safeText(form.observaciones);
    const words = safeObs.split(' ');
    let line = '';
    let ly2 = ry - 8;
    const obsMaxW = RW - 4;
    words.forEach(w => {
      const testLine = line ? line + ' ' + w : w;
      if (f.widthOfTextAtSize(testLine, 6) > obsMaxW && line) {
        p1.drawText(line, { x: RX + 2, y: ly2, size: 6, font: f, color: K });
        ly2 -= 8;
        line = w;
      } else line = testLine;
    });
    if (line) p1.drawText(line, { x: RX + 2, y: ly2, size: 6, font: f, color: K });
  }
  ry -= 44;

  // ESCALA DE GLASGOW (Detallada)
  sectionHeader(p1, 'Escala de Coma de Glasgow', RX, ry - 13, RW, fb);
  ry -= 16;
  const glasgowTotal = form.glasgow_total || form.glasgow_score || '';
  const glasgowItems = [
    ['Ocular (E)', form.glasgow_ocular, { '4': 'Espontánea', '3': 'A la voz', '2': 'Al dolor', '1': 'Ninguna' }],
    ['Verbal (V)', form.glasgow_verbal, { '5': 'Orientada', '4': 'Confusa', '3': 'Inapropiada', '2': 'Incomprensible', '1': 'Ninguna' }],
    ['Motor (M)', form.glasgow_motor, { '6': 'Obedece', '5': 'Localiza', '4': 'Retira', '3': 'Flexión', '2': 'Extensión', '1': 'Ninguna' }],
  ];
  glasgowItems.forEach(([lbl, val, map]) => {
    p1.drawText(lbl.toUpperCase(), { x: RX, y: ry - 2, size: 5.5, font: fb, color: K });
    if (val) {
      const desc = map[val] || '';
      p1.drawText(`${val} - ${desc}`, { x: RX + 60, y: ry - 2, size: 6, font: f, color: K });
    }
    ry -= 10;
  });
  p1.drawText('TOTAL:', { x: RX, y: ry - 2, size: 6, font: fb, color: K });
  borderBox(p1, RX + 30, ry - 10, 24, 10);
  if (glasgowTotal) p1.drawText(String(glasgowTotal), { x: RX + 36, y: ry - 8, size: 8, font: fb, color: K });
  p1.drawText('/ 15', { x: RX + 56, y: ry - 6, size: 5.5, font: f, color: GR });
  ry -= 16;

  // ESCALA PREHOSPITALARIA CINCINNATI
  sectionHeader(p1, 'Escala Prehospitalaria de Cincinnati', RX, ry - 13, RW, fb);
  ry -= 16;
  const cinc = [
    ['Asimetría Facial', form.cincinnati_asimetria_facial],
    ['Paresia de los Brazos', form.cincinnati_paresia_brazos],
    ['Alteración del Lenguaje', form.cincinnati_alteracion_lenguaje],
  ];
  cinc.forEach(([lbl, val]) => {
    p1.drawText(lbl.toUpperCase(), { x: RX, y: ry - 2, size: 5.5, font: fb, color: K });
    chkLabel(p1, val === 'SI', 'Sí', RX + 120, ry - 8, f, fb);
    chkLabel(p1, val === 'NO', 'No', RX + 136, ry - 8, f, fb);
    ry -= 12;
  });
  ry -= 4;

  // ESCALA DE TRAUMA
  sectionHeader(p1, 'Escala de Trauma', RX, ry - 13, RW, fb);
  ry -= 16;
  const traumaCols = ['(A)GCS', '(B)PAS', '(C)FR', '(D)Esf.Resp', '(E)Llen.Cap', 'Pts'];
  const tw2 = RW / traumaCols.length;
  traumaCols.forEach((col, i) => {
    borderBox(p1, RX + i * tw2, ry - 10, tw2, 10);
    p1.drawText(col.toUpperCase(), { x: RX + i * tw2 + 1, y: ry - 8, size: 5, font: fb, color: N });
  });
  ry -= 10;
  const traumaData = [
    ['14-15', '>90', '10-24', 'Normal', 'Normal', '5'],
    ['11-13', '70-89', '25-35', '—', '—', '4'],
    ['8-10', '50-69', '>36', '—', '—', '3'],
    ['5-7', '<50', '—', 'Normal', 'Normal', '2'],
    ['3-4', 'Sin Pulso', '1-9', 'Retardado', '—', '1'],
    ['0', '—', '—', 'Retracción', 'Ausente', '0'],
  ];
  traumaData.forEach(row => {
    row.forEach((cell, i) => {
      borderBox(p1, RX + i * tw2, ry - 9, tw2, 9);
      p1.drawText(cell.toUpperCase(), { x: RX + i * tw2 + 1, y: ry - 7, size: 4.5, font: f, color: K });
    });
    ry -= 9;
  });
  p1.drawText('PUNTUACIÓN TOTAL (A+B+C+D+E):', { x: RX, y: ry - 6, size: 5.5, font: fb, color: K });
  borderBox(p1, RX + 110, ry - 12, 28, 12);
  if (form.trauma_total) p1.drawText(String(form.trauma_total), { x: RX + 116, y: ry - 8, size: 8, font: fb, color: K });
  ry -= 16;


  // ── DATOS RECIÉN NACIDO + DESTINO ──
  // Hard-floor: never go below 110pt from bottom (footer is at ~55pt)
  const FLOOR_Y = 110;
  const rawBotY = Math.min(ly, ry) - 4;
  let botY = Math.max(rawBotY, FLOOR_Y);

  // Height needed for Datos Recién Nacido section (grew due to APGAR table + Madre + Post-Parto)
  const maternoH = 104;
  const datosH = 125;
  const totalMatsH = maternoH + datosH;
  const botFits = botY - totalMatsH > 55; // stays above footer line

  const drawDatosMadre = (page, startY, width) => {
    sectionHeader(page, 'Datos de la Madre', LX, startY - 13, width, fb);
    let lly = startY - 16;
    fieldLine(page, 'Gesta', form.gesta, LX, lly - 4, 50, f, fb);
    fieldLine(page, 'Cesáreas', form.cesareas, LX + 54, lly - 4, 50, f, fb);
    fieldLine(page, 'Para', form.para, LX + 108, lly - 4, 36, f, fb);
    fieldLine(page, 'Abortos', form.abortos, LX + 148, lly - 4, 44, f, fb);
    fieldLine(page, 'FUM', form.fum, LX + 196, lly - 4, width - 196, f, fb);
    lly -= 14;
    fieldLine(page, 'Sem. Gestación', form.semanas_gestacion, LX, lly - 4, 110, f, fb);
    fieldLine(page, 'Fecha Prob. Parto', form.fecha_probable_parto, LX + 114, lly - 4, width - 114, f, fb);
    lly -= 14;
    fieldLine(page, 'Hora Contracciones', form.hora_contracciones, LX, lly - 4, 100, f, fb);
    fieldLine(page, 'Frecuencia', form.frec_contracciones, LX + 104, lly - 4, 66, f, fb);
    fieldLine(page, 'Duración', form.dur_contracciones, LX + 174, lly - 4, width - 174, f, fb);
    lly -= 16;

    sectionHeader(page, 'Datos Post-Parto', LX, lly - 13, width, fb);
    lly -= 16;
    fieldLine(page, 'Hora Nacimiento', form.hora_nacimiento, LX, lly - 4, 110, f, fb);
    fieldLine(page, 'Lugar', form.lugar_nacimiento, LX + 114, lly - 4, width - 114, f, fb);
    lly -= 14;
    page.drawText('PLACENTA EXPULSADA:', { x: LX, y: lly - 2, size: 5.5, font: fb, color: K });
    chk(page, form.placenta_expulsada, LX + 78, lly - 9);
    lly -= 14;
    return lly;
  };

  const drawDatosRN = (page, startY, width) => {
    sectionHeader(page, 'Datos Recién Nacido / APGAR', LX, startY - 13, width, fb);
    let by = startY - 16;

    // Producto & Sexo (Row 1)
    page.drawText('PRODUCTO:', { x: LX, y: by - 2, size: 5.5, font: fb, color: K });
    let ro2 = LX + 40;
    ro2 = chkLabel(page, form.prod_vivo, 'Vivo', ro2, by - 8, f, fb);
    ro2 = chkLabel(page, form.prod_muerto, 'Muerto', ro2, by - 8, f, fb);
    page.drawText('SEXO:', { x: ro2 + 4, y: by - 2, size: 5.5, font: fb, color: K });
    ro2 += 22;
    ro2 = chkLabel(page, form.recien_sexo === 'MASC', 'Masc', ro2, by - 8, f, fb);
    chkLabel(page, form.recien_sexo === 'FEM', 'Fem', ro2, by - 8, f, fb);
    by -= 12;

    // APGAR Table
    const apgarCols = ['Signo', '0', '1', '2'];
    const cw = [60, 60, 60, 60]; // 240 width total
    const ax = LX + 10;

    // Header
    let cx = ax;
    apgarCols.forEach((col, i) => {
      borderBox(page, cx, by - 10, cw[i], 10);
      page.drawText(col.toUpperCase(), { x: cx + 2, y: by - 8, size: 5, font: fb, color: N });
      cx += cw[i];
    });
    by -= 10;

    const apgarData = [
      ['(A) Apariencia', form.apgar_apariencia, ['Cianosis', 'Acrocianosis', 'Rosado']],
      ['(P) Pulso / FC', form.apgar_pulso, ['Ausente', '< 100 lpm', '> 100 lpm']],
      ['(G) Gesticulación', form.apgar_gesticulacion, ['Sin resp.', 'Muecas', 'Llanto']],
      ['(A) Actividad', form.apgar_actividad, ['Flacidez', 'Flexión ext.', 'Mov. activos']],
      ['(R) Respiración', form.apgar_respiracion, ['Ausente', 'Irregular', 'Regular']],
    ];

    apgarData.forEach(row => {
      cx = ax;
      const [lbl, val, opts] = row;
      const vInt = val !== '' ? parseInt(val) : -1;

      // Col 1: Label
      borderBox(page, cx, by - 10, cw[0], 10);
      page.drawText(lbl.toUpperCase(), { x: cx + 2, y: by - 7, size: 4.5, font: fb, color: K });
      cx += cw[0];

      // Cols 2, 3, 4: Options
      opts.forEach((optText, i) => {
        const isSel = vInt === i;
        if (isSel) {
          page.drawRectangle({ x: cx, y: by - 10, width: cw[i + 1], height: 10, color: G });
        } else {
          borderBox(page, cx, by - 10, cw[i + 1], 10);
        }
        page.drawText(optText, { x: cx + 2, y: by - 7, size: 4.5, font: isSel ? fb : f, color: isSel ? W : K });
        cx += cw[i + 1];
      });
      by -= 10;
    });

    // Total Row
    const t = form.apgar_total;
    let rankText = '';
    let rankColor = GR;
    if (t !== '' && t !== undefined) {
      const tv = parseInt(t);
      if (tv >= 7) { rankText = 'ÓPTIMO'; rankColor = rgb(0.13, 0.77, 0.36); }
      else if (tv >= 4) { rankText = 'DEPRESIÓN MODERADA'; rankColor = rgb(0.91, 0.70, 0.03); }
      else { rankText = 'DEPRESIÓN SEVERA'; rankColor = rgb(0.93, 0.26, 0.26); }
    }

    page.drawText('TOTAL APGAR:', { x: ax, y: by - 8, size: 6, font: fb, color: K });
    borderBox(page, ax + 50, by - 12, 24, 12);
    if (t) page.drawText(String(t), { x: ax + 56, y: by - 8, size: 8, font: fb, color: K });

    if (rankText) {
      const rW = fb.widthOfTextAtSize(rankText, 6) + 6;
      page.drawRectangle({ x: ax + 78, y: by - 12, width: rW, height: 12, color: rankColor });
      page.drawText(rankText, { x: ax + 81, y: by - 8, size: 6, font: fb, color: W });
    }

    by -= 18;
    const finalY = fullFieldLine(page, 'Producto', form.prod_texto, LX, by - 10, LW, f, fb);
    return finalY - 6; // Return final Y position reached safely below wrapped text
  };

  const drawDestino = (page, startY, x) => {
    sectionHeader(page, 'Destino', x, startY - 13, 100, fb);
    let dy2 = startY - 16;
    chkLabel(page, form.destino_trasladado, 'Trasladado', x, dy2 - 8, f, fb);
    dy2 -= 11;
    chkLabel(page, form.destino_no_trasladado, 'No Trasladado', x, dy2 - 8, f, fb);
    dy2 -= 11;
    chkLabel(page, form.destino_fuga, 'Fuga', x, dy2 - 8, f, fb);
    return dy2 - 12; // Return final Y position reached
  };

  if (botFits) {
    const finalDatosMadre_Y = drawDatosMadre(p1, botY, LW);
    const finalDatosRN_Y = drawDatosRN(p1, finalDatosMadre_Y, LW);
    const finalDestino_Y = drawDestino(p1, botY, RX);
    botY = Math.min(finalDatosRN_Y, finalDestino_Y);
  }
  // (if !botFits, these sections are drawn on p2 after Zona de Lesión below)



  // ─────────────── PAGE 2 ───────────────
  const p2 = doc.addPage([PW, PH]);
  let p2y = PH - M;

  // Page 2 header (thin)
  p2.drawRectangle({ x: M, y: p2y - 20, width: PW - 2 * M, height: 20, color: G });
  if (logo) p2.drawImage(logo, { x: M + 4, y: p2y - 18, width: 16, height: 16 });
  p2.drawText('CUERPO DE RESCATE DE ENSENADA, A.C. — FRAP', { x: M + 24, y: p2y - 13, size: 9, font: fb, color: W });
  p2.drawText(safeText(`Folio: ${form.folio || '—'}   Fecha: ${form.fecha || ''}   Paciente: ${form.nombre_paciente || ''}`), { x: M + 24, y: p2y - 19, size: 6, font: f, color: W });
  p2y -= 26;

  // ZONA DE LESIÓN — Page 2: silhouettes left + zone chips right
  const lesionZonas = Array.isArray(form.lesion_zonas) ? form.lesion_zonas : [];
  {
    const SH2W = PW - 2 * M;
    sectionHeader(p2, 'Zona de Lesión', M, p2y - 13, SH2W, fb);
    p2y -= 16;

    // ── Left panel: body silhouettes ─────────────────────────────────────────
    const bodyH = 110;           // total height allocated for figures
    const bodyW = 220;           // total width for both figures side-by-side
    const figW = Math.floor((bodyW - 12) / 2);  // width of each figure
    const figH = bodyH - 8;     // height for the figure itself (below title)

    for (let side = 0; side < 2; side++) {
      const fx = M + 2 + side * (figW + 6);
      const fy = p2y - 6;         // top of this figure (pdf y goes up)
      const fw = figW;
      const fh = figH;

      // Figure title
      const figTitle = side === 0 ? 'ANTERIOR' : 'POSTERIOR';
      p2.drawText(figTitle, {
        x: fx + fw / 2 - fb.widthOfTextAtSize(figTitle, 4.5) / 2,
        y: fy - 5, size: 4.5, font: fb, color: N
      });

      // ── Organic SVG silhouette via drawSvgPath ──────────────────────────────
      // SVG viewBox: 0 0 100 280  → scale to fit figW × (fh-8)
      const svgVW = 100; const svgVH = 280;
      const figBodyH = fh - 8;
      const s = Math.min(fw / svgVW, figBodyH / svgVH);
      // pdf-lib drawSvgPath: y = SVG's y=0 mapped to PDF space (top of figure)
      const svgX = fx + (fw - svgVW * s) / 2; // center horizontally
      const svgY = fy - 8;                      // top of figure in PDF coords

      const svgZones = side === 0 ? SVG_FRONT_ZONES : SVG_BACK_ZONES;

      // Pass 1: base fill for all zones
      svgZones.forEach(zone => {
        p2.drawSvgPath(zone.d, {
          x: svgX, y: svgY, scale: s,
          color: rgb(0.98, 0.97, 0.95),
          borderColor: rgb(0.62, 0.67, 0.72),
          borderWidth: 0.4,
        });
      });

      // Pass 2: red overlay for injured zones
      svgZones.forEach(zone => {
        if (!lesionZonas.includes(zone.id)) return;
        p2.drawSvgPath(zone.d, {
          x: svgX, y: svgY, scale: s,
          color: rgb(0.86, 0.10, 0.10),
          borderColor: rgb(0.72, 0.05, 0.05),
          borderWidth: 0.6,
          opacity: 0.50,
        });
      });
    }

    // ── Right panel: zone chips ───────────────────────────────────────────────
    if (lesionZonas.length > 0) {
      const ZONE_LABELS2 = {
        cabeza: 'Cabeza', cara: 'Cara', cuello: 'Cuello',
        hombro_der: 'Hombro Der.', hombro_izq: 'Hombro Izq.',
        torax: 'Tórax/Pecho', abdomen: 'Abdomen', pelvis: 'Pelvis/Cadera',
        brazo_der: 'Brazo Der.', brazo_izq: 'Brazo Izq.',
        antebrazo_der: 'Antebrazo Der.', antebrazo_izq: 'Antebrazo Izq.',
        mano_der: 'Mano Der.', mano_izq: 'Mano Izq.',
        muslo_der: 'Muslo Der.', muslo_izq: 'Muslo Izq.',
        rodilla_der: 'Rodilla Der.', rodilla_izq: 'Rodilla Izq.',
        pierna_der: 'Pierna Der.', pierna_izq: 'Pierna Izq.',
        pie_der: 'Pie Der.', pie_izq: 'Pie Izq.',
        craneo_d: 'Cráneo (D.)', nuca: 'Nuca/Cervical',
        hombro_der_d: 'Hombro Der.(D.)', hombro_izq_d: 'Hombro Izq.(D.)',
        espalda_alta: 'Espalda Alta', espalda_baja: 'Espalda Baja',
        lumbar: 'Lumbar/Sacro', gluteo_der: 'Glúteo Der.', gluteo_izq: 'Glúteo Izq.',
        brazo_der_d: 'Brazo Der.(D.)', brazo_izq_d: 'Brazo Izq.(D.)',
        antebrazo_der_d: 'Antebrazo Der.(D.)', antebrazo_izq_d: 'Antebrazo Izq.(D.)',
        mano_der_d: 'Mano Der.(D.)', mano_izq_d: 'Mano Izq.(D.)',
        muslo_der_d: 'Muslo Der.(D.)', muslo_izq_d: 'Muslo Izq.(D.)',
        pantorrilla_der: 'Pantorrilla Der.', pantorrilla_izq: 'Pantorrilla Izq.',
        talon_der: 'Talón Der.', talon_izq: 'Talón Izq.',
      };
      const chipsX = M + bodyW + 8;
      const chipsMaxW = SH2W - bodyW - 8;
      const chipsStartY = p2y - 10;
      p2.drawText('ZONAS AFECTADAS:', { x: chipsX, y: chipsStartY, size: 5, font: fb, color: N });
      const labels2 = lesionZonas.map(id => ZONE_LABELS2[id] || id);
      const chipH2 = 8; const chipPad2 = 4; const chipRowGap2 = 3;
      let chipX2 = chipsX; let chipY2 = chipsStartY - 8;
      labels2.forEach(lbl => {
        const chipW2 = f.widthOfTextAtSize(lbl, 5.5) + chipPad2 * 2 + 6;
        if (chipX2 + chipW2 > chipsX + chipsMaxW) { chipX2 = chipsX; chipY2 -= (chipH2 + chipRowGap2); }
        p2.drawRectangle({ x: chipX2, y: chipY2 - chipH2, width: chipW2, height: chipH2, color: rgb(0.97, 0.92, 0.92), borderColor: rgb(0.82, 0.15, 0.15), borderWidth: 0.5 });
        p2.drawText(safeText(`• ${lbl}`), { x: chipX2 + chipPad2, y: chipY2 - chipH2 + 1.8, size: 5.5, font: f, color: rgb(0.72, 0.05, 0.05) });
        chipX2 += chipW2 + 4;
      });
    }

    p2y -= bodyH + 6;
  }

  // If Datos Recién Nacido didn't fit on page 1, render here
  if (!botFits) {
    const finalDatosMadre_Y2 = drawDatosMadre(p2, p2y, LW);
    const finalDatosRN_Y2 = drawDatosRN(p2, finalDatosMadre_Y2, LW);
    const finalDestino_Y2 = drawDestino(p2, p2y, RX);
    p2y = Math.min(finalDatosRN_Y2, finalDestino_Y2) - 10;
  }

  const svRows = form.sv_rows || [];

  if (svRows.length > 0 && svRows.some(r => Object.values(r).some(v => v))) {
    sectionHeader(p2, 'Signos Vitales', M, p2y - 13, PW - 2 * M, fb);
    p2y -= 16;
    const svCols = ['Hora', 'FC', 'FR', 'TAS', 'TAD', 'Temp', 'SpO2', 'Glucosa', 'EKG', 'Pupilas', 'Piel', 'Llen.Cap', 'Dolor'];
    const svFields = ['hora', 'fc', 'fr', 'tas', 'tad', 'temp', 'spo2', 'glucosa', 'ekg', 'pupilas', 'piel', 'llenado_cap', 'dolor'];
    const svColW = (PW - 2 * M) / svCols.length;
    // Header row
    svCols.forEach((col, i) => {
      p2.drawRectangle({ x: M + i * svColW, y: p2y - 10, width: svColW, height: 10, color: N });
      p2.drawText(col.toUpperCase(), { x: M + i * svColW + 1, y: p2y - 8, size: 4.5, font: fb, color: W });
    });
    p2y -= 10;
    // Data rows
    svRows.forEach(row => {
      if (!row || typeof row !== 'object') return; // Safety check
      svFields.forEach((fld, i) => {
        borderBox(p2, M + i * svColW, p2y - 10, svColW, 10);
        if (row[fld]) p2.drawText(safeText(row[fld]).substring(0, 10), { x: M + i * svColW + 1, y: p2y - 8, size: 5, font: f, color: K });
      });
      p2y -= 10;
    });
    p2y -= 6;
  }

  // OVERFLOW CHECK: If we don't have enough space left for legal signatures and footer, wrap to Page 3
  let footerPage = p2;
  let totalPages = 2;
  if (p2y < 240) {
    footerPage = doc.addPage([PW, PH]);
    totalPages = 3;
    p2y = PH - M;
    // Page 3 header
    footerPage.drawRectangle({ x: M, y: p2y - 20, width: PW - 2 * M, height: 20, color: G });
    if (logo) footerPage.drawImage(logo, { x: M + 4, y: p2y - 18, width: 16, height: 16 });
    footerPage.drawText('CUERPO DE RESCATE DE ENSENADA, A.C. — FRAP', { x: M + 24, y: p2y - 13, size: 9, font: fb, color: W });
    footerPage.drawText(safeText(`Folio: ${form.folio || '—'}   Fecha: ${form.fecha || ''}   Paciente: ${form.nombre_paciente || ''}`), { x: M + 24, y: p2y - 19, size: 6, font: f, color: W });
    p2y -= 26;

    // We must draw a footer on Page 2
    p2.drawLine({ start: { x: M, y: 30 + 10 }, end: { x: PW - M, y: 30 + 10 }, color: G, thickness: 0.5 });
    const now2 = new Date();
    p2.drawText(`Creado por: ${form.creado_por || 'Usuario'} • Fecha: ${now2.toLocaleString('es-MX')}`, { x: M, y: 30, size: 6, font: f, color: rgb(0.5, 0.5, 0.5) });
    p2.drawText('FRAP – Formulario de Rescate y Atención Prehospitalaria — Cuerpo de Rescate de Ensenada, A.C.', { x: M, y: 30 - 8, size: 5.5, font: fb, color: G });
    p2.drawText(`Página 2 de ${totalPages}`, { x: PW - 60, y: 30, size: 7, font: f, color: K });
  }

  // NEGATIVA (left) | CONSENTIMIENTO (right)
  const halfW = (PW - 2 * M - 10) / 2;
  sectionHeader(footerPage, 'Negativa a Recibir Atención / Ser Trasladado Eximente de Responsabilidad', M, p2y - 13, halfW, fb);
  sectionHeader(footerPage, 'Consentimiento Informado', M + halfW + 10, p2y - 13, halfW, fb);
  p2y -= 18;

  const legalNeg = 'DECLARO QUE NO ACEPTO LAS RECOMENDACIONES DEL PERSONAL DE LA AMBULANCIA DEL CUERPO DE RESCATE EN CUANTO AL (TRATAMIENTO) Y/O (TRASLADO) A UN HOSPITAL; POR LO QUE EXIMO AL CUERPO DE RESCATE Y A DICHAS PERSONAS DE TODA RESPONSABILIDAD QUE PUDIERA DERIVAR AL HABER RESPETADO Y CUMPLIDO MI DECISIÓN.';
  const legalCon = 'PREVIO A UNA DETALLADA EXPLICACIÓN DOY MI CONSENTIMIENTO A SER TRASLADADO Y/O ATENDIDO POR EL PERSONAL DEL CUERPO DE RESCATE A.C.';

  const drawWrapped = (page, text, x, y, maxW, size, font) => {
    const words = text.split(' ');
    let line = '';
    let cy = y;
    words.forEach(w => {
      const testLine = line ? line + ' ' + w : w;
      const textWidth = font.widthOfTextAtSize(testLine, size);
      if (textWidth > maxW && line) {
        page.drawText(line, { x, y: cy, size, font, color: K });
        cy -= size + 2;
        line = w;
      } else {
        line = testLine;
      }
    });
    if (line) page.drawText(line, { x, y: cy, size, font, color: K });
    return cy - size - 2;
  };

  const negEndY = drawWrapped(footerPage, legalNeg, M + 2, p2y - 4, halfW - 4, 5.5, f);
  const conEndY = drawWrapped(footerPage, legalCon, M + halfW + 12, p2y - 4, halfW - 4, 5.5, f);

  p2y = Math.min(negEndY, conEndY) - 6;

  // Signatures row 1: Negativa (paciente + testigo) | Consentimiento (paciente + familiar)
  const sigW = halfW / 2 - 4;
  const sigH = 50;
  const sigLabels1 = [
    { label: 'NOMBRE/FIRMA DEL PACIENTE', sig: form.firma_pac_negativa, nombre: form.nombre_pac_negativa, x: M },
    { label: 'NOMBRE/FIRMA DEL TESTIGO', sig: form.firma_testigo, nombre: form.nombre_testigo, x: M + sigW + 8 },
    { label: 'NOMBRE/FIRMA DEL PACIENTE', sig: form.firma_pac_consentimiento, nombre: form.nombre_pac_consentimiento, x: M + halfW + 10 },
    { label: 'NOMBRE/FIRMA DE FAMILIAR O TUTOR', sig: form.firma_familiar, nombre: form.nombre_familiar, x: M + halfW + 10 + sigW + 8 },
  ];

  for (const s of sigLabels1) {
    borderBox(footerPage, s.x, p2y - sigH, sigW, sigH);
    if (s.sig) {
      const img = await embedSignature(doc, s.sig);
      if (img) footerPage.drawImage(img, { x: s.x + 2, y: p2y - sigH + 16, width: sigW - 4, height: sigH - 20 });
    }
    footerPage.drawLine({ start: { x: s.x + 2, y: p2y - sigH + 14 }, end: { x: s.x + sigW - 2, y: p2y - sigH + 14 }, color: GR, thickness: 0.4 });
    if (s.nombre) footerPage.drawText(safeText(s.nombre), { x: s.x + 2, y: p2y - sigH + 6, size: 5.5, font: f, color: K });
    footerPage.drawText(s.label.toUpperCase(), { x: s.x + 2, y: p2y - sigH + 1, size: 4.5, font: fb, color: GR });
  }
  p2y -= sigH + 20;

  // AUTORIDADES QUE INTERVINIERON — guard ensures no overlap with sigs above
  const autW = 240;
  const autX = (PW - autW) / 2;
  // Minimum space needed: section header (13) + sig box (56) + padding (20) = ~90pt
  if (p2y < 100) p2y = 100;
  sectionHeader(footerPage, 'Autoridades que Intervinieron', autX, p2y - 13, autW, fb);
  p2y -= 20;

  if (form.autoridades_extra) {
    p2y = drawWrapped(footerPage, form.autoridades_extra, autX + 2, p2y - 2, autW - 4, 6.5, f) - 10;
  }

  const autSigs = [
    { label: 'ENTREGA PACIENTE', sig: form.firma_entrega, nombre: form.nombre_entrega, title: 'NOMBRE Y FIRMA' },
    { label: 'MÉDICO QUE RECIBE', sig: form.firma_medico, nombre: form.nombre_medico, title: 'NOMBRE Y FIRMA' },
  ];
  const autSigW = 100;
  let autSigX = autX + 10;
  for (const as of autSigs) {
    footerPage.drawText(as.label.toUpperCase(), { x: autSigX, y: p2y - 6, size: 6, font: fb, color: K });
    borderBox(footerPage, autSigX, p2y - 56, autSigW, 50);
    if (as.sig) {
      const img = await embedSignature(doc, as.sig);
      if (img) footerPage.drawImage(img, { x: autSigX + 2, y: p2y - 56 + 16, width: autSigW - 4, height: 30 });
    }
    footerPage.drawLine({ start: { x: autSigX + 2, y: p2y - 56 + 14 }, end: { x: autSigX + autSigW - 2, y: p2y - 56 + 14 }, color: GR, thickness: 0.4 });
    if (as.nombre) footerPage.drawText(safeText(as.nombre), { x: autSigX + 2, y: p2y - 56 + 6, size: 5.5, font: f, color: K });
    footerPage.drawText(as.title, { x: autSigX + 2, y: p2y - 56 + 1, size: 4.5, font: fb, color: GR });
    autSigX += autSigW + 20;
  }

  // Footer for the final page (Page 2 or Page 3)
  const footY = 30;
  footerPage.drawLine({ start: { x: M, y: footY + 10 }, end: { x: PW - M, y: footY + 10 }, color: G, thickness: 0.5 });
  const now = new Date();
  const createdStr = `Creado por: ${form.creado_por || 'Usuario'} • Fecha: ${now.toLocaleString('es-MX')}`;
  footerPage.drawText(createdStr, { x: M, y: footY, size: 6, font: f, color: rgb(0.5, 0.5, 0.5) });
  footerPage.drawText('FRAP – Formulario de Rescate y Atención Prehospitalaria — Cuerpo de Rescate de Ensenada, A.C.', { x: M, y: footY - 8, size: 5.5, font: fb, color: G });
  footerPage.drawText(`Página ${totalPages} de ${totalPages}`, { x: PW - 60, y: footY, size: 7, font: f, color: K });

  // Add page 1 footer
  p1.drawLine({ start: { x: M, y: 18 }, end: { x: PW - M, y: 18 }, color: G, thickness: 0.5 });
  p1.drawText('FRAP – Formulario de Rescate y Atención Prehospitalaria — Cuerpo de Rescate de Ensenada, A.C.', { x: M, y: 8, size: 5.5, font: fb, color: G });
  p1.drawText(`Página 1 de ${totalPages}`, { x: PW - 60, y: 8, size: 7, font: f, color: K });

  return doc.save();
}
